<template>
  <div class="notif-popup">
    <!-- Header -->
    <div class="notif-head">
      <div class="notif-head-left">
        <label v-if="visibleNotifications.length > 0" class="notif-cb-label" title="Selecionar todas">
          <input
            ref="selectAllEl"
            type="checkbox"
            class="notif-cb"
            :checked="isAllSelected"
            @change="toggleSelectAll"
          />
        </label>
        <span class="notif-title">Notificações</span>
      </div>
      <div class="notif-head-right">
        <template v-if="hasSelected">
          <button class="notif-act-btn notif-act-clear" type="button" @click="requestAction('limpar')">
            Limpar
          </button>
          <button class="notif-act-btn notif-act-view" type="button" @click="requestAction('visualizar')">
            Visualizar
          </button>
        </template>
        <button class="notif-x-btn" type="button" aria-label="Fechar" @click="$emit('close')">✕</button>
      </div>
    </div>

    <!-- Confirm bar -->
    <div v-if="confirmAction" class="notif-confirm-bar">
      <span class="notif-confirm-text">
        Tem certeza que quer <strong>{{ confirmAction }}</strong>
        {{ selectedIds.size }} notificação{{ selectedIds.size !== 1 ? 'ões' : '' }}?
      </span>
      <div class="notif-confirm-btns">
        <button class="notif-confirm-yes" type="button" :disabled="acting" @click="executeAction">
          {{ acting ? '...' : 'Sim' }}
        </button>
        <button class="notif-confirm-cancel" type="button" :disabled="acting" @click="confirmAction = null">
          Cancelar
        </button>
      </div>
    </div>

    <!-- Skeleton -->
    <div v-if="showSkeleton" class="notif-list">
      <SkeletonRow v-for="i in 4" :key="i" :columns="[70, 90, 30]" />
    </div>

    <!-- Empty state -->
    <div v-else-if="notifications.length === 0" class="notif-empty">Nenhuma notificação.</div>

    <!-- List -->
    <div v-else class="notif-list">
      <div
        v-for="item in visibleNotifications"
        :key="item.id"
        class="notif-item"
        :class="{ 'notif-item--sel': selectedIds.has(item.id) }"
      >
        <label class="notif-cb-label" @click.stop>
          <input
            type="checkbox"
            class="notif-cb"
            :checked="selectedIds.has(item.id)"
            @change="toggleSelect(item.id)"
          />
        </label>
        <button class="notif-body" type="button" @click="$emit('open-notification', item)">
          <div class="notif-item-title">
            {{ item.title }}
            <span v-if="!item.readAt" class="notif-pill">novo</span>
          </div>
          <div class="notif-item-text">{{ item.message }}</div>
          <div v-if="item.createdAt" class="notif-item-when">{{ formatDateTime(item.createdAt) }}</div>
        </button>
      </div>
      <button class="notif-view-all" type="button" @click="$emit('view-all')">
        Ver todas as notificações
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watchEffect } from 'vue'
import type { PropType } from 'vue'
import type { AppNotification } from '@/store/notifications'
import { notificationsStore } from '@/store/notifications'
import { formatDateTime } from '@/utils/date'
import SkeletonRow from '@/components/skeleton/SkeletonRow.vue'

export default defineComponent({
  name: 'NotificationsPanel',
  components: { SkeletonRow },
  props: {
    notifications: {
      type: Array as PropType<AppNotification[]>,
      required: true
    }
  },
  emits: ['close', 'open-notification', 'view-all'],
  setup(props) {
    const VISIBLE_LIMIT = 8
    const selectedIds = ref(new Set<string>())
    const confirmAction = ref<'limpar' | 'visualizar' | null>(null)
    const acting = ref(false)
    const selectAllEl = ref<HTMLInputElement | null>(null)

    const visibleNotifications = computed(() => props.notifications.slice(0, VISIBLE_LIMIT))
    const hasSelected = computed(() => selectedIds.value.size > 0)
    const isAllSelected = computed(
      () => visibleNotifications.value.length > 0 && selectedIds.value.size === visibleNotifications.value.length
    )
    const isSomeSelected = computed(() => selectedIds.value.size > 0 && !isAllSelected.value)
    const showSkeleton = computed(() => notificationsStore.state.loading && props.notifications.length === 0)

    watchEffect(() => {
      if (selectAllEl.value) {
        selectAllEl.value.indeterminate = isSomeSelected.value
      }
    })

    function toggleSelect(id: string) {
      const next = new Set(selectedIds.value)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      selectedIds.value = next
    }

    function toggleSelectAll() {
      if (isAllSelected.value) {
        selectedIds.value = new Set()
      } else {
        selectedIds.value = new Set(visibleNotifications.value.map((n) => n.id))
      }
    }

    function requestAction(action: 'limpar' | 'visualizar') {
      confirmAction.value = action
    }

    async function executeAction() {
      if (!confirmAction.value) return
      acting.value = true
      try {
        const ids = Array.from(selectedIds.value)
        if (confirmAction.value === 'visualizar') {
          await notificationsStore.markManyRead(ids)
        } else {
          await notificationsStore.deleteMany(ids)
        }
        selectedIds.value = new Set()
        confirmAction.value = null
      } catch {
        // ignore
      } finally {
        acting.value = false
      }
    }

    return {
      selectedIds,
      confirmAction,
      acting,
      selectAllEl,
      visibleNotifications,
      hasSelected,
      isAllSelected,
      showSkeleton,
      toggleSelect,
      toggleSelectAll,
      requestAction,
      executeAction,
      formatDateTime
    }
  }
})
</script>

<style scoped>
.notif-popup {
  position: fixed;
  right: 20px;
  top: 76px;
  width: min(460px, calc(100vw - 40px));
  max-height: 70vh;
  background: #fff;
  border: 1px solid rgba(31, 30, 26, 0.14);
  border-radius: 14px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.16);
  z-index: 30;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ── Header ── */
.notif-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(31, 30, 26, 0.1);
  flex-shrink: 0;
  gap: 8px;
}

.notif-head-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.notif-head-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

.notif-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--ink, #201f1e);
}

/* ── Checkbox ── */
.notif-cb-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  flex-shrink: 0;
}

.notif-cb {
  width: 15px;
  height: 15px;
  cursor: pointer;
  accent-color: var(--accent, #0078d4);
  flex-shrink: 0;
}

/* ── Action buttons ── */
.notif-act-btn {
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid transparent;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
}

.notif-act-clear {
  background: rgba(164, 38, 44, 0.08);
  color: #a4262c;
  border-color: rgba(164, 38, 44, 0.2);
}
.notif-act-clear:hover {
  background: rgba(164, 38, 44, 0.15);
}

.notif-act-view {
  background: rgba(0, 120, 212, 0.08);
  color: #0078d4;
  border-color: rgba(0, 120, 212, 0.2);
}
.notif-act-view:hover {
  background: rgba(0, 120, 212, 0.15);
}

.notif-x-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: var(--ink-muted, #605e5c);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.12s;
}
.notif-x-btn:hover {
  background: rgba(31, 30, 26, 0.06);
  color: var(--ink, #201f1e);
}

/* ── Confirm bar ── */
.notif-confirm-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 12px;
  background: #fef9ec;
  border-bottom: 1px solid #f0d97a;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.notif-confirm-text {
  font-size: 13px;
  color: #5a4800;
}

.notif-confirm-btns {
  display: flex;
  gap: 6px;
}

.notif-confirm-yes {
  padding: 4px 14px;
  border-radius: 6px;
  border: none;
  background: #d4452f;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.12s;
}
.notif-confirm-yes:hover:not(:disabled) {
  background: #b83520;
}
.notif-confirm-yes:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.notif-confirm-cancel {
  padding: 4px 14px;
  border-radius: 6px;
  border: 1px solid rgba(31, 30, 26, 0.2);
  background: #fff;
  color: var(--ink, #201f1e);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s;
}
.notif-confirm-cancel:hover:not(:disabled) {
  background: rgba(31, 30, 26, 0.05);
}
.notif-confirm-cancel:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ── Empty ── */
.notif-empty {
  padding: 20px 14px;
  font-size: 13px;
  color: var(--ink-muted, #605e5c);
}

/* ── List ── */
.notif-list {
  overflow-y: auto;
  flex: 1;
}

.notif-item {
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid rgba(31, 30, 26, 0.08);
  background: #fff;
  transition: background 0.1s;
}

.notif-item:last-child {
  border-bottom: none;
}

.notif-item--sel {
  background: rgba(31, 30, 26, 0.06);
}

.notif-item .notif-cb-label {
  padding: 10px 6px 10px 12px;
}

.notif-body {
  flex: 1;
  border: none;
  background: transparent;
  text-align: left;
  padding: 10px 14px 10px 6px;
  cursor: pointer;
  min-width: 0;
}

.notif-item:not(.notif-item--sel):hover {
  background: rgba(31, 30, 26, 0.03);
}

.notif-item--sel:hover {
  background: rgba(31, 30, 26, 0.09);
}

.notif-item-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: var(--ink, #201f1e);
}

.notif-item-text {
  margin-top: 3px;
  font-size: 12px;
  line-height: 1.4;
  color: var(--ink-muted, #605e5c);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.notif-item-when {
  margin-top: 3px;
  font-size: 11px;
  color: var(--ink-faint, var(--ink-muted, #8a8886));
  font-variant-numeric: tabular-nums;
}

.notif-pill {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 999px;
  background: #d4452f;
  color: #fff;
  font-weight: 600;
}

/* ── View all ── */
.notif-view-all {
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: transparent;
  color: var(--accent, #0078d4);
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  text-align: center;
  transition: background 0.12s;
}
.notif-view-all:hover {
  background: rgba(0, 120, 212, 0.06);
}
</style>
